import { type AhaMenuItem } from '@aha-app/aha-base/dist/components/aha-menu-item';
import { type ApplicationController } from '@aha-app/mvc';
import ApplicationModel from 'components/framework/ApplicationModel';
import { MutationBatcher } from 'components/framework/MutationBatcher';
import { type MenuContent } from '../ActionMenuItem';

export interface RemoteAttributes {
  href: string;
  method?: 'post' | 'get' | 'put' | 'patch' | 'delete';
}

class RecordAction<RecordType extends ApplicationModel = ApplicationModel> {
  isRemoteLink = false;
  recordTypes: string[] = [];
  emptySubmenuContent = 'No options available';
  menuItemType: AhaMenuItem['type'] | undefined;
  _mutationBatcher: MutationBatcher | undefined;

  /**
   * @param selectedRecords The complete array of records selected by the user
   */
  constructor(public selectedRecords: RecordType[]) {}

  /**
   * The title of the action in the context menu
   */
  get title() {
    return 'Unknown action';
  }

  get icon() {
    return '';
  }

  /**
   * Whether or not this action displays a submenu.
   */
  get hasSubmenu(): boolean {
    return false;
  }

  get firstRecord(): RecordType | undefined {
    return this.selectedRecords[0];
  }

  get singleRecordType() {
    return this.selectedRecords.every(
      record => record.typename === this.firstRecord?.typename
    );
  }

  /**
   * A mutation batcher used to batch any mutations performed on the records.
   */
  get mutationBatcher() {
    if (!this._mutationBatcher) {
      this._mutationBatcher = new MutationBatcher({ reactivelyUpdate: true });
    }

    return this._mutationBatcher;
  }

  /**
   * If the action displays a submenu of options, prepare the items here.
   */
  prepareSubmenuItems(): Promise<MenuContent> | MenuContent {
    return [];
  }

  /**
   * Whether we should show this action for a given record.
   *
   * @param {ApplicationModel} record The record for which the action should be shown
   */
  showForRecord(record: ApplicationModel): record is RecordType {
    return (
      record instanceof ApplicationModel &&
      this.recordTypes.includes(record.typename)
    );
  }

  /**
   * Whether we should show this action for the selected records.
   */
  showForSelectedRecords() {
    return this.selectedRecords.some(record => this.showForRecord(record));
  }

  /**
   * Perform the action for a group of records. The array of records has been filtered with
   * the showForRecord method, so it will only include records that can/should be executed.
   *
   * @param {ApplicationModel[]} records The record for which the action should be shown
   * @param {object} [data] Optional data object.
   * @param {controller} [ApplicationController] Optional controller
   */
  async runForRecords(
    records: RecordType[],
    data: object = {},
    controller?: ApplicationController
  ) {
    // Implement by passing in a value in the constructor
  }

  remoteLinkAttributes(records: RecordType[]): RemoteAttributes | undefined {
    // Override with remote link attributes
    return undefined;
  }
}

export default RecordAction;
